import { MutationCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { type ReactNode, useState } from "react";
import { Toaster } from "@/components/ui/toast";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SessionProvider } from "@/app/session";
import { TenantProvider } from "@/app/tenant-provider";
import { errorMessage, toast } from "@/lib/toast";

/**
 * Deliberate, non-aggressive caching defaults.
 *
 * The app is read-heavy but consistency-sensitive: report send/regenerate
 * status, card assignment and money-code usage all need to reflect the
 * latest mutation quickly. A short staleTime avoids redundant refetches on
 * remount without letting stale state linger the way an aggressive cache
 * would. Individual queries override this where a longer/shorter window is
 * justified (see each feature's hooks).
 *
 * `mutationCache.onError` is a blanket safety net: every mutation in the
 * app shows an error toast unless it opts out via `meta: { suppressErrorToast:
 * true }` (auth flows already show a contextual inline error and would
 * otherwise show the same failure twice). This guarantees no mutation can
 * fail silently, without needing an `onError` added to every single one —
 * success feedback still needs per-mutation wording (a generic "it worked"
 * isn't useful), so that's added individually where it's currently missing.
 */
function createQueryClient(): QueryClient {
  return new QueryClient({
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.suppressErrorToast) {
          return;
        }
        toast.error(
          typeof mutation.meta?.errorTitle === "string" ? mutation.meta.errorTitle : "Something went wrong",
          { description: errorMessage(error) },
        );
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: 1,
        refetchOnWindowFocus: false,
      },
      mutations: {
        retry: 0,
      },
    },
  });
}

export function AppProviders({ children }: { children: ReactNode }) {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <SessionProvider>
          <TooltipProvider delay={200}>
            {children}
            <Toaster />
          </TooltipProvider>
        </SessionProvider>
      </TenantProvider>
    </QueryClientProvider>
  );
}
